import React, { useCallback, useEffect, useRef, useState } from 'react';
import { MdArrowForward, MdCheckCircle, MdDevices, MdInfoOutline, MdOutlineSchedule, MdPause, MdPlayArrow, MdRefresh, MdRestore, MdSettings, MdSystemUpdate, MdWarningAmber, MdWifiOff } from 'react-icons/md';
import { lightOnline } from './lightOrganization';
import './deviceUpdates.css';

const API = process.env.REACT_APP_API_URL;
const environmentLabels = { prod: 'Production', dev: 'Development', local: 'Local' };
const OFFLINE_MS = 30 * 60 * 1000;
const osPhaseLabels = {
  installing: 'Installing', installed: 'Installed, waiting', waiting_for_quiet_hours: 'Waiting for quiet hours',
  waiting_for_idle: 'Waiting until idle', rebooting: 'Restarting', trial: 'Checking new OS', rolling_back: 'Restoring previous OS',
};

async function request(path, body, method = 'POST') {
  const response = await fetch(`${API}${path}`, {
    credentials: 'include', method: body ? method : 'GET',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) throw new Error(data?.error || data?.message || (response.status === 401 ? 'Your session expired. Sign in again.' : 'The request failed. Check your connection and try again.'));
  if (!data) throw new Error('The server returned an invalid response. Please try again.');
  return data;
}

export function isOffline(device, now = Date.now()) {
  return !device.last_seen_at || now - new Date(device.last_seen_at).getTime() > OFFLINE_MS;
}

// App releases: each device follows its OS line (Beta = newest, all devices = released version).
export function appStatus(device, releases = []) {
  const report = device.snapshot || {};
  const target = device.target_release_id;
  if (report.phase === 'maintenance_required') return 'Failed';
  if (target && report.target === target && report.channel_revision === device.revision) {
    if (report.result === 'rolled_back') return 'Rolled back';
    if (report.result === 'failed') return 'Failed';
  }
  if (!target) return 'No release yet';
  if (report.software_version === target && report.phase === 'idle') return 'Confirmed';
  const sequence = id => releases.find(item => item.id === id)?.manifest.signed.sequence;
  if (sequence(report.software_version) > sequence(target)) return 'Newer installed';
  return device.enabled ? 'Pending' : 'Paused';
}

// OS images follow the same two stages; the device reports its running image and slot.
export function osOutcome(device, images = []) {
  const report = device.os_snapshot;
  const target = device.os_target_image_id;
  if (!report?.supported) return 'Not on Steffi OS';
  if (report.phase === 'rolled_back' && (!target || report.target === target)) return 'Rolled back';
  if (report.phase === 'failed') return 'Failed';
  if (osPhaseLabels[report.phase]) return osPhaseLabels[report.phase];
  if (!target) return 'Running';
  if (report.version === target) return 'Confirmed';
  const sequence = id => images.find(item => item.id === id)?.sequence;
  if (sequence(report.version) > sequence(target)) return 'Newer installed';
  return device.os_enabled ? 'Pending' : 'Paused';
}

function formatSize(bytes) {
  if (!bytes) return null;
  return bytes >= 1024 ** 3 ? `${(bytes / 1024 ** 3).toFixed(1)} GB` : `${Math.round(bytes / 1024 ** 2)} MB`;
}

// An OS line is named after the Steffi OS images that run it (e.g. debian-12 -> steffios-2026.10.2, 2026.11.1).
function lineLabel(line, images = []) {
  const [os, version] = line.split('-');
  const ids = images.filter(image => os === 'debian' && String(image.debian_version || '').split('.')[0] === version).map(image => image.id);
  if (ids.length) return `${ids.slice(0, 2).join(', ')}${ids.length > 2 ? ', …' : ''}`;
  return os === 'debian' ? `Debian ${version} (no Steffi OS image yet)` : line;
}

function deviceCount(count) {
  return `${count} ${count === 1 ? 'device' : 'devices'}`;
}

function formatDate(seconds, fallback) {
  return new Date(seconds ? seconds * 1000 : fallback).toLocaleDateString();
}

function StatusBadge({ status }) {
  const tone = ['Confirmed', 'Running', 'Online'].includes(status) ? 'success' : ['Failed', 'Rolled back'].includes(status) ? 'error'
    : status === 'Installed, waiting' || status.startsWith('Waiting') ? 'warning'
    : ['Pending', 'Installing', 'Restarting', 'Checking new OS'].includes(status) ? 'blue' : 'neutral';
  return <span className={`du-badge du-badge--${tone}`}><span className="du-status-dot" />{status}</span>;
}

function OfflineBadge({ device }) {
  return isOffline(device)
    ? <span className="du-badge du-badge--neutral"><MdWifiOff aria-hidden="true" />Offline{device.last_seen_at ? ` since ${new Date(device.last_seen_at).toLocaleString()}` : ''}</span>
    : <StatusBadge status="Online" />;
}

function UpdaterInstalled({ device }) {
  return <span aria-label={`Updater installation for ${device.public_id}`}>
    <span className={`du-badge du-badge--${device.updater_installed === true ? 'success' : device.updater_installed === false ? 'warning' : 'neutral'}`}>
      <span className="du-status-dot" />{device.updater_installed === true ? 'Installed' : device.updater_installed === false ? 'Not installed' : 'Unknown'}
    </span>
    {device.updater_checked_at && <small className="du-updater-checked">Last checked {new Date(device.updater_checked_at).toLocaleString()}</small>}
  </span>;
}

function Facts({ label, facts }) {
  return <dl className="du-release-info" aria-label={label}>
    {facts.filter(Boolean).map(([name, value]) => <div key={name}><dt>{name}</dt><dd>{value}</dd></div>)}
  </dl>;
}

// What differs between app releases and OS images; the page layout is shared.
const KINDS = {
  app: {
    noun: 'release', field: 'release_id', discovery: '/admin/updates/discovery',
    lines: data => (data.lines || []).map(line => ({ key: line.os_line, label: lineLabel(line.os_line, data.os_images), revision: line.revision, paused: line.paused,
      beta: line.beta_release_id, stable: line.stable_release_id, path: `/admin/updates/lines/${encodeURIComponent(line.os_line)}` })),
    items: (data, line) => data.releases.filter(item => item.os_line === line.key),
    devices: (data, line) => data.devices.filter(device => device.os_line === line.key),
    archivePath: id => `/admin/updates/releases/${encodeURIComponent(id)}/archive`,
    stage: device => device.stage, target: device => device.target_release_id, pinned: device => device.pinned,
    sequence: item => item.manifest.signed.sequence,
    status: (device, data) => appStatus(device, data.releases),
    summary: item => ({ date: formatDate(item.manifest.signed.published_at, item.created_at), notes: item.manifest.signed.notes,
      restoredFrom: item.manifest.signed.restored_from, environment: item.environment }),
    info: (item, data) => {
      const signed = item.manifest.signed;
      const running = data.devices.filter(device => device.snapshot?.software_version === item.id).length;
      return [['Published', formatDate(signed.published_at, item.created_at)], ['Notes', signed.notes || 'No notes'],
        signed.restored_from && ['Restores', signed.restored_from],
        ["What's new", signed.changes?.length ? <ul>{signed.changes.map(change => <li key={change}>{change}</li>)}</ul> : 'No commit list'],
        ['Commit', signed.source_commit ? <a className="du-version" href={`https://github.com/SteffiCare/SteffiCare/commit/${signed.source_commit}`} target="_blank" rel="noreferrer">{signed.source_commit.slice(0, 8)}</a> : 'Unknown'],
        ['Size', formatSize(signed.size) || 'Unknown'],
        ['Linux packages', (signed.linux_packages || []).map(entry => `${entry.name} ${entry.version}`).join(', ') || 'None'],
        ['Running on', deviceCount(running)]];
    },
    text: { beta: 'Try a release on beta-test devices first. They install it as soon as they are idle.',
      all: 'Every other device, including newly set up ones, installs this version during quiet hours.',
      dev: 'Every development device installs the newest release as soon as it is idle.',
      empty: <>No app releases yet. Publish one with <code>make -C device release VERSION=… ENV=…</code>, then check for new releases.</> },
  },
  os: {
    noun: 'OS image', field: 'image_id', discovery: '/admin/updates/os-discovery',
    lines: data => (data.os ? [{ key: 'os', label: 'Steffi OS', revision: data.os.revision, paused: data.os.paused,
      beta: data.os.beta_image_id, stable: data.os.stable_image_id, path: '/admin/updates/os' }] : []),
    items: data => data.os_images || [],
    devices: data => data.devices,
    archivePath: id => `/admin/updates/os-images/${encodeURIComponent(id)}/archive`,
    stage: device => device.os_stage, target: device => (device.os_snapshot?.supported ? device.os_target_image_id : null),
    pinned: device => device.os_pinned, sequence: item => item.sequence,
    status: (device, data) => osOutcome(device, data.os_images),
    summary: item => ({ date: formatDate(null, item.created_at), notes: item.notes }),
    info: (item, data) => {
      const running = data.devices.filter(device => device.os_snapshot?.version === item.id).length;
      return [['Published', formatDate(null, item.created_at)], ['Notes', item.notes || 'No notes'],
        ['System', [item.os_version, item.debian_version && `Debian ${item.debian_version}`].filter(Boolean).join(' · ') || 'Unknown'],
        ['Kernel', item.kernel || 'Unknown'], ['Download', formatSize(item.size) || 'Unknown'],
        ['Built-in app', item.baseline_app_release || 'Unknown'], ['Running on', deviceCount(running)]];
    },
    text: { beta: 'Try an OS image on beta-test devices first. They install it when idle and restart once (about 3 minutes).',
      all: 'Every other device installs this image during quiet hours. If it fails its health check, the device returns to its previous OS.',
      dev: 'Every development device installs the newest OS image as soon as it is idle.',
      empty: 'No OS images yet. Build one with device/release/os_image.py, then check for new OS images.' },
  },
};

function StageCard({ kind, name, step, line, current, devices, items, busy, mutate, counts }) {
  const config = KINDS[kind];
  const usable = items.filter(item => !item.archived_at);
  const [version, setVersion] = useState(current || usable[0]?.id || '');
  const [choosing, setChoosing] = useState(false);
  const [ids, setIds] = useState([]);
  const sequence = id => { const item = items.find(entry => entry.id === id); return item ? config.sequence(item) : 0; };
  const older = Boolean(current && version && sequence(version) < sequence(current));
  const blocked = kind === 'app' && older;
  const beta = name === 'beta';
  function send(deviceIds) {
    const target = deviceIds ? deviceCount(deviceIds.length) : beta ? 'all beta-test devices' : 'all devices';
    const warning = older ? ` This is older than ${current}: devices go back to it and restart once.` : '';
    if (!window.confirm(`Release ${version} to ${target} of ${line.label}?${warning}`)) return;
    mutate(`${line.path}/release`, { [config.field]: version, expected_revision: line.revision, stage: name, ...(deviceIds ? { device_ids: deviceIds } : {}) })
      .then(result => { if (result) { setChoosing(false); setIds([]); } });
  }
  return <article className={`du-channel du-channel--${beta ? 'test' : 'stable'}`} aria-label={beta ? 'Beta stage' : 'All devices stage'}>
    <div className="du-channel-top"><div className="du-channel-title"><span className="du-channel-step">{step}</span><h4>{beta ? 'Beta-test devices' : 'All devices'}</h4></div></div>
    <p className="du-channel-description">{beta ? config.text.beta : config.text.all}</p>
    <div className="du-channel-release"><span>Current version</span><strong>{current || 'Not released yet'}</strong></div>
    {counts(devices)}
    <label className="du-release-picker du-stage-picker">Version to release
      <select aria-label={`Version for ${beta ? 'beta-test devices' : 'all devices'}`} value={version} disabled={busy || !usable.length} onChange={event => setVersion(event.target.value)}>
        {usable.map((item, index) => <option key={item.id} value={item.id}>{item.id}{index === 0 ? ' (newest)' : ''}</option>)}
      </select>
    </label>
    {blocked && <small className="du-error-text">Devices never go back to an older release. Use Restore in the list below.</small>}
    <div className="du-channel-actions">
      <button className="du-button du-button--promote" disabled={busy || !version || blocked || version === current} onClick={() => send(null)}>{beta ? 'Release to all beta-test devices' : 'Release to all devices'}<MdArrowForward aria-hidden="true" size={17} /></button>
      <button className="du-button du-button--text" aria-expanded={choosing} disabled={busy || !version || blocked || !devices.length} onClick={() => setChoosing(!choosing)}>Choose devices</button>
    </div>
    {choosing && <div className="du-choose" aria-label={`Choose ${beta ? 'beta-test devices' : 'devices'}`}>
      {devices.map(device => <label key={device.device_id} className="du-checkbox"><input type="checkbox" aria-label={`Choose ${device.public_id}`} checked={ids.includes(device.device_id)}
        onChange={event => setIds(event.target.checked ? [...ids, device.device_id] : ids.filter(id => id !== device.device_id))} />{device.public_id}</label>)}
      <button className="du-button du-button--secondary" disabled={busy || !ids.length} onClick={() => send(ids)}>Release to {deviceCount(ids.length)}</button>
    </div>}
  </article>;
}

function Rollout({ kind, data, busy, mutate, setNotice }) {
  const config = KINDS[kind];
  const prod = data.environment === 'prod';
  const lines = config.lines(data);
  const [lineKey, setLineKey] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [info, setInfo] = useState(null);
  const [restoring, setRestoring] = useState(null);
  const line = lines.find(item => item.key === lineKey) || lines[0];
  if (!line) return <p className="du-empty">{config.text.empty}</p>;
  const devices = config.devices(data, line);
  const allItems = config.items(data, line);
  const items = allItems.filter(item => showArchived || !item.archived_at);
  const status = device => config.status(device, data);
  const counts = group => <div className="du-channel-members"><span><MdDevices aria-hidden="true" size={17} />{deviceCount(group.length)}</span>
    <div>{Object.entries(group.reduce((all, device) => ({ ...all, [status(device)]: (all[status(device)] || 0) + 1 }), {}))
      .map(([name, count]) => <span key={name} className="du-count">{name}: {count}</span>)}</div></div>;
  const newest = allItems.find(item => !item.archived_at)?.id;
  async function archive(item, archived) {
    if (archived && !window.confirm(`Archive ${item.id}? It disappears from the list and can no longer be released.`)) return;
    const result = await mutate(config.archivePath(item.id), { archived });
    if (result?.devices_running) setNotice(`${item.id} is archived. ${result.devices_running} ${result.devices_running === 1 ? 'device still runs' : 'devices still run'} it.`);
  }
  function pause() {
    if (window.confirm(line.paused ? `Resume updates for ${line.label}?` : `Pause all updates for ${line.label}? Devices keep their current version.`)) {
      mutate(`${line.path}/pause`, { paused: !line.paused, expected_revision: line.revision });
    }
  }
  function toggleBeta(device, checked) {
    const question = checked ? `Mark ${device.public_id} as a beta-test device? It gets new versions first, for releases and OS images.`
      : `Remove ${device.public_id} from beta-test devices? It then gets the version released to all devices.`;
    if (window.confirm(question)) mutate(`/admin/devices/${device.device_id}/beta`, { expected_revision: device.revision, is_beta: checked }, 'PATCH');
  }
  const stageKey = `${line.key}:${line.revision}`;
  return <>
    {lines.length > 1 && <><div className="du-kind-switch du-line-switch" role="group" aria-label="OS generation">
      {lines.map(item => <button key={item.key} className={`du-button du-button--secondary ${item === line ? 'du-kind--active' : ''}`} aria-pressed={item === line} onClick={() => setLineKey(item.key)}>
        {item.label} · {deviceCount(config.devices(data, item).length)}</button>)}
    </div><p className="du-line-help">Releases are built for one OS generation. Each device only gets releases for the Steffi OS image it runs.</p></>}
    <div className="du-channels">
      {prod ? <>
        <StageCard key={`beta:${stageKey}`} kind={kind} name="beta" step="01" line={line} current={line.beta} devices={devices.filter(device => device.is_beta)} items={allItems} busy={busy} mutate={mutate} counts={counts} />
        <StageCard key={`stable:${stageKey}`} kind={kind} name="stable" step="02" line={line} current={line.stable} devices={devices.filter(device => !device.is_beta)} items={allItems} busy={busy} mutate={mutate} counts={counts} />
      </> : <article className="du-channel du-channel--stable" aria-label="All devices stage">
        <div className="du-channel-top"><div className="du-channel-title"><span className="du-channel-step">01</span><h4>All devices</h4></div></div>
        <p className="du-channel-description">{config.text.dev}</p>
        <div className="du-channel-release"><span>Newest {config.noun}</span><strong>{newest || 'None yet'}</strong></div>
        {counts(devices)}
      </article>}
    </div>
    <div className="du-rollout-actions">
      {line.paused && <p className="du-message du-message--error"><MdWarningAmber aria-hidden="true" />Updates for {line.label} are paused.</p>}
      <button className="du-button du-button--text" disabled={busy} onClick={pause}>{line.paused ? <MdPlayArrow aria-hidden="true" /> : <MdPause aria-hidden="true" />}{line.paused ? 'Resume updates' : 'Pause updates'}</button>
    </div>
    <div className="du-section-heading"><h3>{kind === 'app' ? 'Releases' : 'OS images'}</h3>
      <label className="du-checkbox"><input type="checkbox" checked={showArchived} onChange={event => setShowArchived(event.target.checked)} />Show archived</label></div>
    <ul className="du-history">{items.map(item => {
      const summary = config.summary(item);
      return <li key={item.id} className="du-history-row" aria-label={`${kind === 'app' ? 'Release' : 'OS image'} ${item.id}`}>
        <div className="du-history-main">
          <span className="du-version">{item.id}</span>
          {summary.environment && <span className={`du-badge du-badge--${summary.environment === 'prod' ? 'success' : 'neutral'}`}>{summary.environment === 'prod' ? 'Production' : 'Development'}</span>}
          {prod && item.id === line.beta && <span className="du-badge du-badge--blue">Beta</span>}
          {item.id === (prod ? line.stable : newest) && <span className="du-badge du-badge--success">All devices</span>}
          {summary.restoredFrom && <span className="du-badge du-badge--warning">Restores {summary.restoredFrom}</span>}
          {item.archived_at && <span className="du-badge du-badge--neutral">Archived</span>}
          <small>{summary.date}{summary.notes ? ` · ${summary.notes}` : ''}</small>
        </div>
        <div className="du-history-actions">
          <button className="du-button du-button--text" aria-label={`Info for ${item.id}`} aria-expanded={info === item.id} onClick={() => setInfo(info === item.id ? null : item.id)}><MdInfoOutline aria-hidden="true" /></button>
          {kind === 'app' && <button className="du-button du-button--text" onClick={() => setRestoring(restoring === item.id ? null : item.id)}><MdRestore aria-hidden="true" />Restore</button>}
          <button className="du-button du-button--text" disabled={busy} onClick={() => archive(item, !item.archived_at)}>{item.archived_at ? 'Show again' : 'Archive'}</button>
        </div>
        {info === item.id && <Facts label={`Details for ${item.id}`} facts={config.info(item, data)} />}
        {restoring === item.id && <p className="du-release-note">Devices never install an older release. Publish this one again under a new version on the builder, then check for new releases and release it to a stage:
          <code>make -C device release-restore FROM={item.id} VERSION=&lt;new version&gt; ENV={item.environment}</code></p>}
      </li>;
    })}{!items.length && <li className="du-empty">Nothing published yet.</li>}</ul>
    <div className="du-section-heading du-devices-heading"><div><h3>Devices <span className="du-total">{devices.length}</span></h3>
      <p>{prod ? 'Mark beta-test devices to try new versions first. The mark applies to releases and OS images.' : 'Development devices always get the newest version.'}</p></div><span className="du-auto-refresh">Refreshes every 30 seconds</span></div>
    <div className="du-table-wrap"><table className="du-table">
      <thead><tr><th>Device</th>{prod && <th>Beta-test</th>}{kind === 'app' ? <><th>Updater</th><th>Installed</th></> : <th>Running OS</th>}<th>Target</th><th>Status</th>{kind === 'os' && <th><span className="du-sr-only">Actions</span></th>}</tr></thead>
      <tbody>{devices.map(device => {
        const report = device.os_snapshot || {};
        const state = status(device);
        const error = ['Failed', 'Rolled back'].includes(state) && (kind === 'app' ? device.snapshot?.error : report.error);
        return <tr key={`${device.device_id}:${device.revision}`} aria-label={`${kind === 'app' ? 'Updates' : 'OS'} for ${device.public_id}`}>
          <td><strong>{device.public_id}</strong></td>
          {prod && <td><input type="checkbox" aria-label={`Beta-test device ${device.public_id}`} checked={device.is_beta} disabled={busy}
            onChange={event => toggleBeta(device, event.target.checked)} /></td>}
          {kind === 'app' ? <><td><UpdaterInstalled device={device} /></td><td className="du-version">{device.snapshot?.software_version || 'Unknown'}</td></>
            : <td>{report.supported ? <><span className="du-version">{report.version || 'Unknown'}</span>{report.slot && <small> · slot {report.slot}</small>}</>
              : <small>Old single-partition layout. Flash the Steffi OS card image at the next service visit.</small>}</td>}
          <td><span className="du-version">{config.target(device) || '—'}</span>{config.pinned(device) && <> <span className="du-badge du-badge--warning">Assigned</span></>}</td>
          <td><StatusBadge status={state} /> <OfflineBadge device={device} />
            {error && <small className="du-error-text">{error}</small>}</td>
          {kind === 'os' && <td>{report.supported && <button className="du-button du-button--text" disabled={busy || !report.other_slot}
            title={report.other_slot ? 'Restart into the OS kept in the other slot' : 'The other slot has no known OS yet'}
            onClick={() => window.confirm(`Switch ${device.public_id} to the previous OS? The device restarts when idle.`) && mutate(`/admin/devices/${device.device_id}/os-switch-slot`, { expected_revision: device.revision })}>
            <MdRestore aria-hidden="true" />Switch to previous OS</button>}</td>}
        </tr>;
      })}</tbody>
    </table></div>
    {!devices.length && <p className="du-empty">No devices yet.</p>}
  </>;
}

function QuietHours({ settings, mutate, busy }) {
  const [window, setWindow] = useState(settings.window || { start: '02:00', end: '05:00' });
  return <details className="du-settings">
    <summary className="du-button du-button--text"><MdSettings aria-hidden="true" />Quiet hours settings</summary>
    <form className="du-quiet-hours" onSubmit={event => { event.preventDefault(); mutate('/admin/updates/settings', { expected_revision: settings.revision, window }, 'PATCH'); }}>
    <h3><MdOutlineSchedule aria-hidden="true" /> Quiet hours for all devices</h3>
    <p>Each device uses these hours in its own system timezone. {settings.window ? '' : 'Save a window before devices update.'}</p>
    <div className="du-window-fields">
      <label>Quiet hours start<input type="time" required value={window.start} disabled={busy} onChange={event => setWindow({ ...window, start: event.target.value })} /></label>
      <label>End<input type="time" required value={window.end} disabled={busy} onChange={event => setWindow({ ...window, end: event.target.value })} /></label>
      <button className="du-button du-button--secondary" disabled={busy || window.start === window.end}>Save quiet hours</button>
    </div>
    </form>
  </details>;
}

export default function DeviceUpdates({ light = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [discovery, setDiscovery] = useState('');
  const [kind, setKind] = useState('app');
  const discoveryRun = useRef(0);
  useEffect(() => () => { discoveryRun.current += 1; }, []);
  const refresh = useCallback(async () => {
    const next = await request('/admin/updates/releases');
    const now = Date.now();
    setData(light ? { ...next, devices: lightOnline(next.devices, device => !isOffline(device, now),
      (device, online) => ({ ...device, last_seen_at: online ? new Date(now).toISOString() : null })) } : next);
  }, [light]);
  useEffect(() => {
    refresh().catch(e => setError(e.message));
    const timer = setInterval(() => refresh().catch(e => setError(e.message)), 30000);
    return () => clearInterval(timer);
  }, [refresh]);
  async function mutate(path, body, method) {
    setBusy(true); setError(''); setNotice('');
    try {
      const result = await request(path, body, method);
      await refresh();
      return result;
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function reload() {
    setBusy(true); setError('');
    try { await refresh(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  async function discover() {
    if (busy) return;
    const { discovery: path, noun } = KINDS[kind];
    const run = ++discoveryRun.current;
    setBusy(true); setError(''); setNotice(''); setDiscovery(`Checking for new ${noun}s…`);
    const added = new Set();
    let checked = 0;
    let cursor = null;
    const issues = [];
    try {
      do {
        const job = await request(path, { cursor });
        let result;
        do {
          result = await request(`/admin/updates/discovery/${job.id}`);
          if (discoveryRun.current !== run) return;
          setDiscovery(`Checking ${noun}s… ${checked + result.checked} checked`);
          if (result.status === 'running') await new Promise(resolve => setTimeout(resolve, 1000));
        } while (result.status === 'running' && discoveryRun.current === run);
        if (discoveryRun.current !== run) return;
        if (result.status === 'failed') throw new Error(result.error || 'Discovery failed. Please try again.');
        result.added.forEach(id => added.add(id));
        issues.push(...result.errors);
        checked += result.checked;
        cursor = result.next_cursor;
      } while (cursor && discoveryRun.current === run);
      await refresh();
      if (discoveryRun.current !== run) return;
      setNotice(added.size ? `${added.size} new ${noun}${added.size === 1 ? '' : 's'} found.${data.environment === 'prod' ? ' Beta-test devices get the newest one first.' : ''}` : `No new ${noun}s found.`);
      if (issues.length) setError(issues.map(item => `${item.file}: ${item.error}`).join('; '));
    } catch (e) {
      if (discoveryRun.current === run) setError(e.message);
    } finally {
      if (discoveryRun.current === run) { setBusy(false); setDiscovery(''); }
    }
  }
  if (!data) return <section className="device-updates du-loading" aria-label="Updates">
    <MdSystemUpdate aria-hidden="true" size={28} /><p role={error ? 'alert' : 'status'}>{error || 'Loading device updates…'}</p>
    {error && <button className="du-button du-button--secondary" onClick={reload} disabled={busy}>Try again</button>}
  </section>;
  return <section className="device-updates" aria-labelledby="device-updates-heading" aria-busy={busy}>
    <header className="du-header">
      <div className="du-heading-group"><span className="du-heading-icon"><MdSystemUpdate aria-hidden="true" size={26} /></span><div>
        <h1 id="device-updates-heading">Updates <span className={`du-badge du-badge--${data.environment === 'prod' ? 'warning' : 'neutral'}`}>{environmentLabels[data.environment] || data.environment}</span></h1>
        <p>Small code changes go out as releases, large system changes as OS image releases.</p>
      </div></div>
      <div className="du-kind-switch" role="group" aria-label="Update type">
        {Object.entries({ app: 'Releases', os: 'OS image releases' }).map(([value, label]) => <button key={value} className={`du-button du-button--secondary ${kind === value ? 'du-kind--active' : ''}`} aria-pressed={kind === value} disabled={busy} onClick={() => { setKind(value); setNotice(''); }}>{label}</button>)}
      </div>
      <button className="du-button du-button--secondary" onClick={reload} disabled={busy}><MdRefresh aria-hidden="true" size={18} />Refresh</button>
    </header>
    {error && <p role="alert" className="du-message du-message--error"><MdWarningAmber aria-hidden="true" size={20} />{error}</p>}
    {notice && <p role="status" className="du-message du-message--success"><MdCheckCircle aria-hidden="true" size={20} />{notice}</p>}
    <div className="du-discovery-actions">
      <button className="du-button du-button--primary" disabled={busy} onClick={discover}><MdRefresh aria-hidden="true" />Check for new {KINDS[kind].noun}s</button>
      {discovery && <span role="status">{discovery}</span>}
      <QuietHours key={data.settings.revision} settings={data.settings} mutate={mutate} busy={busy} />
    </div>
    <Rollout key={kind} kind={kind} data={data} busy={busy} mutate={mutate} setNotice={setNotice} />
  </section>;
}
